import React, { useState } from 'react'
import { Endpoint, Language } from './data/types'
import CodeExample from './CodeExample'

interface EndpointCardProps {
    endpoint: Endpoint
    language: Language
}

const EndpointCard: React.FC<EndpointCardProps> = ({ endpoint, language }) => {
    const [activeTab, setActiveTab] = useState<'curl' | 'javascript'>('curl')

    return (
        <div className="endpoint-card" id={endpoint.id}>
            <div className="endpoint-card-header">
                <span className={`endpoint-method ${endpoint.method.toLowerCase()}`}>
                    {endpoint.method}
                </span>
                <span className="endpoint-path">{endpoint.path}</span>
                {endpoint.authentication && (
                    <span className="endpoint-auth-badge">
                        {language === 'pt' ? 'Autenticação Requerida' : 'Auth Required'}
                    </span>
                )}
            </div>

            <div className="endpoint-card-body">
                <h3 className="endpoint-title">{endpoint.title[language]}</h3>
                <p className="endpoint-description">{endpoint.description[language]}</p>

                {endpoint.authType && (
                    <div className="endpoint-section">
                        <h4 className="endpoint-section-title">
                            {language === 'pt' ? 'Autenticação' : 'Authentication'}
                        </h4>
                        <p>{endpoint.authType[language]}</p>
                    </div>
                )}

                {endpoint.parameters && endpoint.parameters.length > 0 && (
                    <div className="endpoint-section">
                        <h4 className="endpoint-section-title">
                            {language === 'pt' ? 'Parâmetros' : 'Parameters'}
                        </h4>
                        <table className="parameters-table">
                            <thead>
                                <tr>
                                    <th>{language === 'pt' ? 'Nome' : 'Name'}</th>
                                    <th>{language === 'pt' ? 'Tipo' : 'Type'}</th>
                                    <th>{language === 'pt' ? 'Obrigatório' : 'Required'}</th>
                                    <th>{language === 'pt' ? 'Descrição' : 'Description'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {endpoint.parameters.map((param) => (
                                    <tr key={param.name}>
                                        <td><code>{param.name}</code></td>
                                        <td><code>{param.type}</code></td>
                                        <td className={param.required ? 'param-required' : 'param-optional'}>
                                            {param.required
                                                ? (language === 'pt' ? 'Sim' : 'Yes')
                                                : (language === 'pt' ? 'Não' : 'No')
                                            }
                                        </td>
                                        <td>{param.description[language]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {endpoint.requestBody && (
                    <div className="endpoint-section">
                        <h4 className="endpoint-section-title">
                            {language === 'pt' ? 'Corpo da Requisição' : 'Request Body'}
                        </h4>
                        <p>{endpoint.requestBody.description[language]}</p>
                        <CodeExample
                            code={JSON.stringify(endpoint.requestBody.example, null, 2)}
                            language="json"
                        />
                    </div>
                )}

                {endpoint.response && (
                    <div className="endpoint-section">
                        <h4 className="endpoint-section-title">
                            {language === 'pt' ? 'Resposta' : 'Response'}
                        </h4>
                        <p>{endpoint.response.description[language]}</p>
                        <CodeExample
                            code={JSON.stringify(endpoint.response.example, null, 2)}
                            language="json"
                        />
                    </div>
                )}

                {endpoint.codeExamples && (
                    <div className="endpoint-section">
                        <h4 className="endpoint-section-title">
                            {language === 'pt' ? 'Exemplos de Código' : 'Code Examples'}
                        </h4>
                        <div className="code-examples-tabs">
                            <button
                                className={`code-example-tab ${activeTab === 'curl' ? 'active' : ''}`}
                                onClick={() => setActiveTab('curl')}
                            >
                                cURL
                            </button>
                            <button
                                className={`code-example-tab ${activeTab === 'javascript' ? 'active' : ''}`}
                                onClick={() => setActiveTab('javascript')}
                            >
                                JavaScript
                            </button>
                        </div>
                        {activeTab === 'curl' && (
                            <CodeExample
                                code={endpoint.codeExamples.curl}
                                language="bash"
                                showHeader={false}
                            />
                        )}
                        {activeTab === 'javascript' && (
                            <CodeExample
                                code={endpoint.codeExamples.javascript}
                                language="javascript"
                                showHeader={false}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default EndpointCard
